import { useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormProvider,
  useFieldArray,
  useForm,
  type Control,
  type SubmitHandler,
} from "react-hook-form";

import { FieldGroupSurface } from "@/components/field-group-surface";
import { SaveStateInline } from "@/components/save-state-inline";
import { SectionBlock } from "@/components/section-block";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  UPDATE_DRAFT_PREVIEW,
  type DraftPreview,
  type UpdateDraftPreviewData,
  type UpdateDraftPreviewVariables,
} from "@/graphql";
import { useAutoSave } from "@/hooks/use-auto-save";
import type { UseAutoSaveState } from "@/hooks/use-auto-save";
import {
  buildSchema,
  type BuildFormInput,
  type BuildFormValues,
} from "@/pages/build/build-schema";

interface BuildFormProps {
  instagramUsername: string;
  draftPreview: DraftPreview;
  onAutoSaveStateChange?: (state: UseAutoSaveState) => void;
  onPreviewDraftChange?: (draftPreview: DraftPreview) => void;
}

const BUTTON_ROW_GRID_TEMPLATE =
  "28px minmax(160px, 0.8fr) minmax(260px, 1.2fr) 96px 32px";

export function BuildForm({
  instagramUsername,
  draftPreview,
  onAutoSaveStateChange,
  onPreviewDraftChange,
}: BuildFormProps) {
  const form = useForm<BuildFormInput, unknown, BuildFormValues>({
    resolver: zodResolver(buildSchema),
    defaultValues: toFormValues(draftPreview),
    mode: "onChange",
  });
  const hydratedDraftIdRef = useRef(draftPreview.id);

  useEffect(() => {
    if (hydratedDraftIdRef.current === draftPreview.id) return;

    hydratedDraftIdRef.current = draftPreview.id;
    form.reset(toFormValues(draftPreview));
  }, [draftPreview, form]);

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "buttons",
  });

  const [updateDraft] = useMutation<
    UpdateDraftPreviewData,
    UpdateDraftPreviewVariables
  >(UPDATE_DRAFT_PREVIEW);

  const saveDraft = async (values: BuildFormInput | BuildFormValues) => {
    await updateDraft({
      variables: {
        instagramUsername,
        hasProfileHeader: values.hasProfileHeader,
        hasIntroduction: values.hasIntroduction,
        introductionText: values.introductionText,
        buttons: values.buttons.map((button) => ({
          text: button.text,
          url: button.url,
          enabled: button.enabled,
        })),
        hasGallery: values.hasGallery,
        galleryTitle: values.galleryTitle,
        galleryId: values.galleryId,
        galleryRedirect: values.galleryRedirect,
        galleryDefaultRedirectUrl: values.galleryDefaultRedirectUrl,
        hasRedirect: values.hasRedirect,
        redirectUrl: values.redirectUrl,
      },
    });
  };

  const onSubmit: SubmitHandler<BuildFormValues> = async (values) => {
    await saveDraft(values);
  };

  const autoSaveState = useAutoSave<BuildFormInput>({
    form,
    onSave: async (values) => {
      await saveDraft(values);
      return true;
    },
    delayMs: 500,
  });

  useEffect(() => {
    onAutoSaveStateChange?.(autoSaveState);
  }, [autoSaveState, onAutoSaveStateChange]);

  const previewValues = form.watch();

  useEffect(() => {
    onPreviewDraftChange?.(
      toDraftPreview(previewDraftBase(draftPreview), previewValues)
    );
  }, [draftPreview, onPreviewDraftChange, previewValues]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((field) => field.id === active.id);
    const newIndex = fields.findIndex((field) => field.id === over.id);

    if (oldIndex >= 0 && newIndex >= 0) {
      move(oldIndex, newIndex);
    }
  }

  const { isSubmitting, isValid } = form.formState;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
        <ToggleSection
          title="Profile Header"
          description="Show your Instagram avatar and username at the top of the page."
          control={form.control}
          name="hasProfileHeader"
        />

        <ToggleSection
          title="Introduction"
          description="Add a short line of copy beneath your profile to introduce the page."
          control={form.control}
          name="hasIntroduction"
        >
          <FieldGroupSurface>
            <FormField
              control={form.control}
              name="introductionText"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Introduction Text</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="This is the live caption"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    What you&apos;d like to show under your profile.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldGroupSurface>
        </ToggleSection>

        <SectionBlock
          title="Buttons"
          description="Add the links you want visitors to click and drag them into the order you want to show."
        >
          <FieldGroupSurface className="space-y-3 bg-neutral-25 p-4 sm:p-5">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((field) => field.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="overflow-x-auto rounded-lg border border-neutral-100 bg-white">
                  <div
                    className="grid min-w-[680px] gap-2 border-b border-neutral-100 px-3 py-2"
                    style={{ gridTemplateColumns: BUTTON_ROW_GRID_TEMPLATE }}
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500"></div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                      Text
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                      Link
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                      Status
                    </div>
                    <div />
                  </div>

                  <div className="divide-y divide-neutral-100">
                    {fields.map((field, index) => (
                      <SortableButtonRow
                        key={field.id}
                        id={field.id}
                        index={index}
                        control={form.control}
                        onRemove={() => remove(index)}
                      />
                    ))}
                  </div>
                </div>
              </SortableContext>
            </DndContext>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => append({ text: "", url: "", enabled: true })}
              className="w-fit px-0 text-sm text-blue-600 hover:bg-transparent hover:text-blue-700"
            >
              <i className="fa-regular fa-circle-plus" />
              Add button
            </Button>
          </FieldGroupSurface>
        </SectionBlock>

        <ToggleSection
          title="Gallery"
          description="Show a gallery below your links and configure how its media should behave."
          control={form.control}
          name="hasGallery"
        >
          <FieldGroupSurface>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="galleryTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gallery Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Gallery title"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormDescription>
                      Make it something creative!
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="galleryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sked account key</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your Sked account key"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormDescription>
                      Get this from Sked in account settings by enabling profile
                      redirect URL.{" "}
                      <a
                        href="https://help.skedsocial.com/what-is-sked-link?utm_source=skedlink&utm_medium=gallery_support"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 underline"
                      >
                        See where to access it.
                      </a>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="galleryDefaultRedirectUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default URL for media</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Default URL link for media"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormDescription>
                      Fallback URL for media that doesn&apos;t have redirect URL
                      set during scheduling.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="galleryRedirect"
                render={({ field }) => (
                  <FormItem className="rounded-xl border border-neutral-100 bg-neutral-25 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 space-y-2">
                        <FormLabel>Redirect to latest post</FormLabel>
                        <FormDescription>
                          Enabling this will send all of your Sked Link traffic
                          to the redirect URL of the latest photo in your feed
                          which is set on Sked.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          size="lg"
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="mt-2" />
                  </FormItem>
                )}
              />
            </div>
          </FieldGroupSurface>
        </ToggleSection>

        <ToggleSection
          title="Redirect"
          description="Send all visitors directly to a single URL instead of showing the rest of the page."
          control={form.control}
          name="hasRedirect"
        >
          <FieldGroupSurface>
            <FormField
              control={form.control}
              name="redirectUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Redirect URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://url.com"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    Adding a URL here will send all of your Sked Link traffic to
                    the one page. Your visitors will not see your buttons or
                    gallery, they will just get sent immediately to this link.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldGroupSurface>
        </ToggleSection>

        <div className="flex flex-wrap items-center gap-4 border-t border-neutral-100 pt-6">
          <Button type="submit" size="sm" disabled={isSubmitting || !isValid}>
            {isSubmitting ? "Saving..." : "Save changes"}
          </Button>
          <SaveStateInline
            isSubmitting={isSubmitting}
            isValid={isValid}
            isDirty={autoSaveState.hasUnsavedChanges}
            isAutoSaving={autoSaveState.isSaving}
            isAutoSavePending={autoSaveState.isPending}
            autoSaveErrorMessage={autoSaveState.errorMessage}
          />
        </div>
      </form>
    </FormProvider>
  );
}

interface ToggleSectionProps {
  title: string;
  description?: string;
  control: Control<BuildFormInput, unknown, BuildFormValues>;
  name: "hasProfileHeader" | "hasIntroduction" | "hasGallery" | "hasRedirect";
  children?: React.ReactNode;
}

function ToggleSection({
  title,
  description,
  control,
  name,
  children,
}: ToggleSectionProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <SectionBlock
          title={title}
          description={description}
          action={
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
              size="lg"
            />
          }
        >
          {field.value ? children : null}
        </SectionBlock>
      )}
    />
  );
}

interface SortableButtonRowProps {
  id: string;
  index: number;
  control: Control<BuildFormInput, unknown, BuildFormValues>;
  onRemove: () => void;
}

function SortableButtonRow({
  id,
  index,
  control,
  onRemove,
}: SortableButtonRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      className="grid min-w-[680px] items-center gap-2 px-3 py-1.5"
      aria-label={`Button row ${index + 1}`}
      role="row"
      // Explicit grid template keeps the editor table-like and prevents rows from collapsing into stacked form blocks.
      style={{
        ...style,
        gridTemplateColumns: BUTTON_ROW_GRID_TEMPLATE,
      }}
    >
      <button
        type="button"
        className="flex h-7 items-center justify-center text-neutral-500 transition-colors hover:text-neutral-700"
        aria-label="Reorder button"
        {...attributes}
        {...listeners}
      >
        <i className="fa-solid fa-grip-vertical" />
      </button>

      <FormField
        control={control}
        name={`buttons.${index}.text`}
        render={({ field }) => (
          <FormItem className="min-w-0 space-y-0">
            <FormControl>
              <Input
                placeholder="Website"
                {...field}
                className="h-7 rounded-sm px-2 text-xs shadow-none"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`buttons.${index}.url`}
        render={({ field }) => (
          <FormItem className="min-w-0 space-y-0">
            <FormControl>
              <Input
                placeholder="https://example.com"
                {...field}
                className="h-7 rounded-sm px-2 text-xs shadow-none"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`buttons.${index}.enabled`}
        render={({ field }) => (
          <FormItem className="space-y-0">
            <FormControl>
              <label className="flex h-7 items-center gap-1.5 whitespace-nowrap font-inter text-xs text-neutral-700">
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  size="sm"
                />
              </label>
            </FormControl>
          </FormItem>
        )}
      />

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove button"
        className="flex h-7 items-center justify-center text-red-500 transition-colors hover:text-red-600"
      >
        <i className="fa-solid fa-trash-can" />
      </button>
    </div>
  );
}

function toFormValues(preview: DraftPreview): BuildFormValues {
  return {
    hasProfileHeader: preview.hasProfileHeader ?? false,
    hasIntroduction: preview.hasIntroduction ?? false,
    introductionText: preview.introductionText ?? "",
    buttons: (preview.buttons ?? []).map((button) => ({
      text: button.text ?? "",
      url: button.url ?? "",
      enabled: button.enabled ?? true,
    })),
    hasGallery: preview.hasGallery ?? false,
    galleryTitle: preview.galleryTitle ?? "",
    galleryId: preview.galleryId ?? "",
    galleryRedirect: preview.galleryRedirect ?? false,
    galleryDefaultRedirectUrl: preview.galleryDefaultRedirectUrl ?? "",
    hasRedirect: preview.hasRedirect ?? false,
    redirectUrl: preview.redirectUrl ?? "",
  };
}

function previewDraftBase(preview: DraftPreview): DraftPreview {
  return {
    ...preview,
    buttons: [...preview.buttons],
  };
}

function toDraftPreview(
  preview: DraftPreview,
  values: BuildFormInput
): DraftPreview {
  return {
    ...preview,
    hasProfileHeader: values.hasProfileHeader,
    hasIntroduction: values.hasIntroduction,
    introductionText: values.introductionText,
    buttons: values.buttons.map((button) => ({
      text: button.text,
      url: button.url,
      enabled: button.enabled,
    })),
    hasGallery: values.hasGallery,
    galleryTitle: values.galleryTitle,
    galleryId: values.galleryId,
    galleryRedirect: values.galleryRedirect,
    galleryDefaultRedirectUrl: values.galleryDefaultRedirectUrl,
    hasRedirect: values.hasRedirect,
    redirectUrl: values.redirectUrl,
  };
}
